import { useIsAdmin } from "@/hooks/useAuth";
import { useGroupSelection } from "@/hooks/useGroupSelection";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components/toast/CustomToast";
import httpClient from "@/config/axios";
import type { NurseResponse } from "@/types/nurse-type";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { useNurseAsOfMonthStore } from "@/store/nurseAsOfMonthStore";

/**
 * 간호사 정보 통합 관리 훅 (수정, 삭제 모두 대응)
 * 백엔드 규격에 따라 전체 리스트를 bulk-update 하는 방식으로 동작합니다.
 */
export default function useNurseMutation() {
  const queryClient = useQueryClient();
  const isAdmin = useIsAdmin();
  const { selectedGroupId } = useGroupSelection();

  return useMutation({
    mutationFn: async (nurses: NurseResponse[]) => {
      const groupId = isAdmin ? selectedGroupId : null;

      // 월 셀렉터 선택월 → period valid_from(그 달부터 발효). 미설정 시 백엔드 today 폴백.
      const { year, month } = useNurseAsOfMonthStore.getState();
      const params = new URLSearchParams();
      if (groupId) params.set("group_id", String(groupId));
      if (year != null && month != null) {
        params.set("year", String(year));
        params.set("month", String(month));
      }
      const qs = params.toString();
      const url = `/nurses/bulk-update${qs ? `?${qs}` : ""}`;

      return await httpClient.post(url, nurses);
    },
    onSuccess: (_, variables) => {
      // 전달된 데이터의 길이를 통해 삭제/수정 구분 (단순 알림용)
      const currentNurses = queryClient
        .getQueriesData<NurseResponse[]>({
          queryKey: QUERY_KEYS.nurses.listScope(selectedGroupId),
        })
        .find(([, data]) => Array.isArray(data))?.[1];

      if (currentNurses && variables.length < currentNurses.length) {
        toast.success("성공적으로 삭제되었습니다.");
      } else {
        toast.success("변경사항이 적용되었습니다.");
      }

      // 데이터 리프레시
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.nurses.listScope(selectedGroupId),
      });
      // 주휴 설정쪽 간호사목록도 리프레시
      queryClient.invalidateQueries({ queryKey: ['nurses', 'fullSchema', isAdmin ? selectedGroupId : undefined] });

    },
    onError: (error: Error) => {
      const message = error.message || "요청 처리 중 오류가 발생했습니다.";
      toast.error(message);
      console.error("Nurse Mutation Error:", message);
    },
  });
}
